/* CK | Bundle Tiers
   The "Menge" option renders as tier cards. Tier variants only carry a price and
   metafields — the cart line is always N x the single variant, so on every
   selection we point the form back at the single and set the quantity to N.
   The CK Bundle Discount function is what actually discounts that line.

   Nothing inside the picker is cached: the theme replaces the whole
   <variant-picker> element on each variant change (ProductInfo.updateOptionValues
   -> HTMLUpdateUtility.viewTransition), so held references go stale after the
   first click. We anchor on <product-info>, which survives, and query down. */
(function () {
  // The quick view re-inserts this script on every open; the first run's
  // listeners already cover later modals.
  if (window.__ckGtBundleTiersLoaded) return;
  window.__ckGtBundleTiersLoaded = true;

  class CkGtBundleTiers {
    constructor(productInfo) {
      this.productInfo = productInfo;
      this.tiers = null;
      this.apply = this.apply.bind(this);
    }

    // Scoped first: a quick view of the PDP's own product shares its form id.
    get form() {
      const id = this.productInfo.getAttribute('form');
      if (!id) return null;
      return this.productInfo.querySelector('form[id="' + CSS.escape(id) + '"]') || document.forms[id] || null;
    }

    // The replaced picker is inserted before the outgoing one, so the first
    // match in document order is always the live element.
    get picker() {
      return this.productInfo.querySelector('variant-picker');
    }

    get inputs() {
      const picker = this.picker;
      return picker ? Array.from(picker.querySelectorAll('.ck-gt-bundle-tier-input')) : [];
    }

    // Static per product, so parse once — but only once a picker actually has it.
    get tierData() {
      if (this.tiers) return this.tiers;

      const node = this.picker?.querySelector('[data-ck-tier-inventory]');
      if (!node) return {};

      try {
        this.tiers = JSON.parse(node.textContent);
      } catch (error) {
        this.tiers = {};
      }
      return this.tiers;
    }

    init() {
      // Delegated, so it keeps working after the picker is swapped out.
      this.productInfo.addEventListener('change', this.apply);
      this.productInfo.addEventListener('product-info:loaded', this.apply);
      this.form?.addEventListener('variant:change', this.apply);

      this.apply();
    }

    cardFor(input) {
      return this.picker?.querySelector('.ck-gt-bundle-tier[for="' + CSS.escape(input.id) + '"]') || null;
    }

    selectedCard() {
      const input = this.inputs.find((candidate) => candidate.checked);
      return input ? this.cardFor(input) : null;
    }

    unitsFor(card) {
      return (card && parseInt(card.dataset.itemsPerPackage, 10)) || 1;
    }

    // Tier stock is the single's stock, since that's what actually ships.
    isTierAvailable(card) {
      const singleId = card && card.dataset.singleVariantId;
      const tier = singleId ? this.tierData[singleId] : null;
      if (!tier || !tier.tracked) return true;
      return tier.inventory >= this.unitsFor(card);
    }

    apply() {
      const card = this.selectedCard();
      if (!card || !this.form) return;

      const singleId = card.dataset.singleVariantId;
      if (singleId) this.setVariantId(singleId);
      this.setQuantity(this.unitsFor(card));
      this.setAddButtonState(this.isTierAvailable(card));
      this.syncCards();
    }

    setVariantId(variantId) {
      const input = this.form.querySelector('input[name="id"]');
      if (input && input.value !== String(variantId)) input.value = variantId;
    }

    // Reuses a quantity input if one is present, so re-enabling a stepper can
    // never produce two `quantity` fields in the same form. The theme rebuilds
    // the form's contents on variant change, so this may need recreating.
    setQuantity(units) {
      let input = this.form.querySelector('input[name="quantity"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        this.form.appendChild(input);
      }
      input.value = String(units);
    }

    setAddButtonState(available) {
      const button = this.form.querySelector('[name="add"]');
      if (!button) return;
      if (available) {
        button.removeAttribute('disabled');
      } else {
        button.setAttribute('disabled', '');
      }
    }

    syncCards() {
      this.inputs.forEach((input) => {
        const card = this.cardFor(input);
        if (!card) return;

        const available = this.isTierAvailable(card);
        card.classList.toggle('ck-gt-bundle-tier--disabled', !available);
        input.disabled = !available;
      });
    }
  }

  function init(root) {
    (root || document).querySelectorAll('product-info').forEach((productInfo) => {
      if (productInfo.__ckBundleTiersInitialized) return;
      if (!productInfo.querySelector('.ck-gt-bundle-tier-input')) return;
      productInfo.__ckBundleTiersInitialized = true;
      new CkGtBundleTiers(productInfo).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('quickview:loaded', function () {
    init(document);
  });
})();
